import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Plus,
  Search,
  ChevronDown,
  Folder,
  Layers,
  Sparkles,
} from 'lucide-react';
import TemplateCard from './TemplateCard';
import TemplateRemixPanel from './TemplateRemixPanel';
import { templatesAPI, templateFoldersAPI, getErrorMessage } from '@/services/api';
import { PostTemplate, TemplateFolder, TemplateCategory } from '@/types/templates';

type SortOption = 'recent' | 'most_used' | 'best_performing' | 'alphabetical';

const TemplateManager: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // State management
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory | 'all'>('all');
  const [sortBy, setSortBy] = useState<SortOption>('recent');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [page, setPage] = useState(1);
  const [remixTemplate, setRemixTemplate] = useState<PostTemplate | null>(null);

  // Debounced search with useCallback to prevent infinite loops
  const [debouncedSearch, setDebouncedSearch] = useState('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch templates
  const { data: templatesData, isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['templates', page, selectedCategory, activeFolderId, debouncedSearch, sortBy],
    queryFn: async () => {
      const response = await templatesAPI.list({
        page,
        limit: 24,
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        folder_id: activeFolderId || undefined,
        search: debouncedSearch || undefined,
        sort_by: sortBy,
      });
      return response.data;
    },
  });

  // Fetch folders
  const { data: foldersData } = useQuery({
    queryKey: ['template-folders'],
    queryFn: async () => {
      const response = await templateFoldersAPI.list();
      return response.data;
    },
  });

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: (name: string) => templateFoldersAPI.create({ name }),
    onSuccess: () => {
      toast.success(t('templates_folder_created', 'Folder created successfully'));
      queryClient.invalidateQueries({ queryKey: ['template-folders'] });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t('templates_folder_error', 'Failed to create folder')));
    },
  });


  // Favorite toggle mutation
  const toggleFavoriteMutation = useMutation({
    mutationFn: (id: string) => templatesAPI.toggleFavorite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t('templates_favorite_error', 'Failed to update favorite')));
    },
  });

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: (id: string) => templatesAPI.delete(id),
    onSuccess: () => {
      toast.success(t('templates_deleted', 'Template deleted'));
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t('templates_delete_error', 'Failed to delete template')));
    },
  });

  // Duplicate template mutation
  const duplicateTemplateMutation = useMutation({
    mutationFn: (id: string) => templatesAPI.duplicate(id),
    onSuccess: () => {
      toast.success(t('templates_duplicated', 'Template duplicated'));
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t('templates_duplicate_error', 'Failed to duplicate template')));
    },
  });

  // Event handlers
  const handleCreateTemplate = useCallback(() => {
    navigate('/templates/new');
  }, [navigate]);

  const handleEditTemplate = useCallback((template: PostTemplate) => {
    navigate(`/templates/${template.id}/edit`);
  }, [navigate]);

  const handleCreateFolder = useCallback((name: string) => {
    if (name.trim()) {
      createFolderMutation.mutate(name);
    }
  }, [createFolderMutation]);


  const folders = foldersData?.folders || [];
  const templates = templatesData?.templates || [];
  const totalTemplates = templatesData?.total || 0;
  const hasMore = (page * 24) < totalTemplates;
  const favoritesCount = templates.filter((t: PostTemplate) => t.is_favorite).length;

  const categories: Array<{ value: TemplateCategory | 'all'; label: string }> = [
    { value: 'all', label: t('templates_category_all', 'All') },
    { value: 'hook', label: t('templates_category_hooks', 'Hook') },
    { value: 'body', label: t('templates_category_body', 'Body') },
    { value: 'cta', label: t('templates_category_cta', 'CTA') },
    { value: 'hashtags', label: t('templates_category_hashtags', 'Hashtags') },
    { value: 'first_comment', label: t('templates_category_first_comment', 'Comment') },
    { value: 'full', label: t('templates_category_full', 'Full') },
  ];

  const sortOptions: Array<{ value: SortOption; label: string }> = [
    { value: 'recent', label: t('templates_sort_recent', 'Recently Updated') },
    { value: 'most_used', label: t('templates_sort_most_used', 'Most Used') },
    { value: 'best_performing', label: t('templates_sort_best_performing', 'Best Performing') },
    { value: 'alphabetical', label: t('templates_sort_alphabetical', 'Alphabetical') },
  ];

  return (
    <div className="h-full flex flex-col bg-n-card overflow-hidden">
      {/* Page Header — fixed */}
      <div className="flex-shrink-0 px-6 pt-6 pb-4">
        <div className="flex items-start gap-3 mb-1">
          <span className="text-4xl leading-none mt-0.5">📋</span>
          <h1 className="text-4xl font-bold text-n-text">{t('templates_title', 'Templates')}</h1>
        </div>
        <p className="text-sm text-n-text-md ml-12">{t('templates_description', 'Save and reuse content across all your platforms.')}</p>
      </div>

      {/* Database View */}
      <div className="flex-1 min-h-0 flex flex-col border-t border-n-border">
        {/* Toolbar — fixed */}
        <div className="flex-shrink-0 flex items-center justify-between gap-4 px-6 pt-4 pb-4 border-b border-n-border bg-n-card z-10">
          {/* Left: Category Tabs */}
          <div className="flex items-center gap-1 flex-wrap">
            {categories.map((cat) => {
              const isActive = selectedCategory === cat.value;
              return (
                <button
                  key={cat.value}
                  onClick={() => {
                    setSelectedCategory(cat.value);
                    setPage(1);
                  }}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border-b-2 transition-colors ${
                    isActive
                      ? 'text-n-text border-n-text'
                      : 'text-n-text-md border-transparent hover:text-n-text'
                  }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* Right: Stats badges, Search, Sort, New Button */}
          <div className="flex items-center gap-2">
            {/* Inline stats badges */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-n-hover text-n-text-md">
                {totalTemplates} {t('templates_total_label', 'Total')}
              </span>
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-n-hover text-n-text-md">
                {favoritesCount} {t('templates_favorites_label', 'Favorites')}
              </span>
            </div>

            {/* Search input */}
            <div className="relative flex-shrink-0 w-40">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-n-text-lt" />
              <input
                type="text"
                placeholder={t('templates_search_placeholder', 'Search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 text-xs bg-n-page border border-n-border rounded hover:border-n-text-lt focus:outline-none focus:ring-1 focus:ring-n-blue placeholder:text-n-text-lt"
              />
            </div>

            {/* Sort dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-n-text-md border border-n-border rounded-md hover:bg-n-hover transition-colors"
              >
                <span>{sortOptions.find((s) => s.value === sortBy)?.label || t('templates_sort', 'Sort')}</span>
                <ChevronDown size={12} />
              </button>

              {showSortDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-n-card border border-n-border rounded-n-sm py-1 shadow-n z-20">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSortBy(option.value);
                        setShowSortDropdown(false);
                        setPage(1);
                      }}
                      className={`flex items-center gap-2.5 w-full px-3 py-2 text-xs text-left ${
                        sortBy === option.value
                          ? 'bg-n-hover text-n-text font-medium'
                          : 'text-n-text-md hover:bg-n-hover'
                      } transition-colors`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Load Starter Templates button */}
            <button
              onClick={async () => {
                try {
                  toast.loading(t('templates_seeding', 'Creating starter templates...'), { id: 'seed' });
                  await templatesAPI.seedDefaults(true);
                  toast.success(t('templates_seeded', '18 starter templates loaded!'), { id: 'seed' });
                  queryClient.invalidateQueries({ queryKey: ['templates'] });
                } catch {
                  toast.error(t('templates_seed_failed', 'Failed to create starters'), { id: 'seed' });
                }
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded border border-n-border text-n-text-md hover:bg-n-hover hover:text-n-text transition-colors"
              title={t('templates_load_starters_desc', 'Replace with 18 AI-powered starter templates')}
            >
              <Sparkles size={14} />
              {t('templates_load_starters', 'Load Starters')}
            </button>

            {/* Compose button */}
            <button
              onClick={() => navigate('/templates/compose')}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded border border-n-border text-n-text hover:bg-n-hover"
            >
              <Layers size={14} />
              {t('templates_compose_title', 'Compose')}
            </button>

            {/* New Template button */}
            <button
              onClick={handleCreateTemplate}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white rounded transition-colors"
              style={{ backgroundColor: '#2383e2' }}
              title={t('templates_new_template', 'New Template')}
            >
              <Plus size={12} />
              {t('templates_new', 'New')}
            </button>
          </div>
        </div>

        {/* Active Filters Row */}
        {(selectedCategory !== 'all' || debouncedSearch || activeFolderId) && (
          <div className="flex-shrink-0 flex items-center gap-2 flex-wrap px-6 py-2 border-b border-n-border text-xs">
            {selectedCategory !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-n-hover text-n-text-md">
                {t('templates_category', 'Category')}{' '}
                <span className="text-n-text-lt font-normal">{t('templates_is', 'is')}</span>{' '}
                <span className="font-semibold">
                  {categories.find((c) => c.value === selectedCategory)?.label || selectedCategory}
                </span>
                <button
                  onClick={() => { setSelectedCategory('all'); setPage(1); }}
                  className="ml-1 text-n-text-lt hover:text-n-text"
                  aria-label={t('templates_clear_filter', 'Clear filter')}
                >
                  ×
                </button>
              </span>
            )}
            {debouncedSearch && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-n-hover text-n-text-md">
                {t('templates_search_filter', 'Search')}:{' '}
                <span className="font-semibold">&ldquo;{debouncedSearch}&rdquo;</span>
                <button
                  onClick={() => setSearchQuery('')}
                  className="ml-1 text-n-text-lt hover:text-n-text"
                  aria-label={t('templates_clear_filter', 'Clear filter')}
                >
                  ×
                </button>
              </span>
            )}
            {activeFolderId && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-n-hover text-n-text-md">
                {t('templates_folder', 'Folder')}
                <button
                  onClick={() => setActiveFolderId(null)}
                  className="ml-1 text-n-text-lt hover:text-n-text"
                  aria-label={t('templates_clear_filter', 'Clear filter')}
                >
                  ×
                </button>
              </span>
            )}
          </div>
        )}

        {/* Folder filter chips (if folders exist) */}
        {folders.length > 0 && (
          <div className="flex-shrink-0 flex items-center gap-2 flex-wrap px-6 py-2 border-b border-n-border text-xs">
            <button
              onClick={() => setActiveFolderId(null)}
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded transition-colors ${
                activeFolderId === null
                  ? 'bg-n-hover text-n-text-md'
                  : 'bg-transparent text-n-text-lt hover:text-n-text-md'
              }`}
            >
              {t('templates_all_folders', 'All')}
            </button>
            {folders.map((folder: TemplateFolder) => (
              <button
                key={folder.id}
                onClick={() => setActiveFolderId(folder.id)}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded transition-colors ${
                  activeFolderId === folder.id
                    ? 'bg-n-hover text-n-text-md'
                    : 'bg-transparent text-n-text-lt hover:text-n-text-md'
                }`}
              >
                <Folder size={12} />
                {folder.name}
              </button>
            ))}
            <button
              onClick={() => {
                const name = prompt(t('templates_folder_create_prompt', 'Folder name:'));
                if (name) handleCreateFolder(name);
              }}
              className="inline-flex items-center gap-1 px-2 py-1 text-n-text-lt hover:text-n-text-md rounded transition-colors"
            >
              <Plus size={12} />
              {t('templates_new_folder', 'New')}
            </button>
          </div>
        )}

        {/* Content Area — scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          {isLoadingTemplates ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-n-hover rounded-lg border border-n-border animate-pulse h-40"
                />
              ))}
            </div>
          ) : templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Folder size={32} className="mx-auto text-n-text-lt mb-3" />
              <h3 className="font-semibold text-n-text-md mb-1">
                {debouncedSearch || selectedCategory !== 'all' || activeFolderId
                  ? t('templates_empty_filters', 'No templates found')
                  : t('templates_empty_title', 'No templates yet')}
              </h3>
              <p className="text-xs text-n-text-lt mb-4 max-w-sm">
                {debouncedSearch || selectedCategory !== 'all' || activeFolderId
                  ? t('templates_empty_adjust', 'Try adjusting your search or filters')
                  : t('templates_empty_description', 'Create your first template to start building amazing content')}
              </p>
              {!debouncedSearch && selectedCategory === 'all' && !activeFolderId && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={async () => {
                      try {
                        toast.loading(t('templates_seeding', 'Creating starter templates...'), { id: 'seed' });
                        await templatesAPI.seedDefaults(true);
                        toast.success(t('templates_seeded', 'Starter templates created!'), { id: 'seed' });
                        queryClient.invalidateQueries({ queryKey: ['templates'] });
                      } catch {
                        toast.error(t('templates_seed_failed', 'Failed to create starters'), { id: 'seed' });
                      }
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white rounded transition-colors"
                    style={{ backgroundColor: '#2383e2' }}
                  >
                    <Sparkles size={12} />
                    {t('templates_load_starters', 'Load Starter Templates')}
                  </button>
                  <span className="text-xs text-n-text-lt">{t('templates_or', 'or')}</span>
                  <button
                    onClick={handleCreateTemplate}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded border border-n-border text-n-text hover:bg-n-hover transition-colors"
                  >
                    <Plus size={12} />
                    {t('templates_create_blank', 'Create from Scratch')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {templates.map((template: PostTemplate) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    onEdit={handleEditTemplate}
                    onDuplicate={(id) => duplicateTemplateMutation.mutate(id)}
                    onDelete={(id) => {
                      if (confirm(t('templates_confirm_delete', 'Delete this template?'))) {
                        deleteTemplateMutation.mutate(id);
                      }
                    }}
                    onToggleFavorite={(id) => toggleFavoriteMutation.mutate(id)}
                    onRemix={(template) => setRemixTemplate(template)}
                  />
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="flex justify-center pt-4 pb-2">
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1.5 text-xs font-medium border border-n-border rounded text-n-text-md hover:bg-n-hover transition-colors"
                  >
                    {t('templates_load_more', 'Load More')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Template Remix Panel Modal */}
      <TemplateRemixPanel
        isOpen={!!remixTemplate}
        onClose={() => setRemixTemplate(null)}
        template={remixTemplate}
        onUseVariation={(_content: string) => {
          setRemixTemplate(null);
          navigate(`/templates/${remixTemplate?.id}/edit`);
        }}
      />
    </div>
  );
};

export default TemplateManager;
